/**
 * Wait until an overlay lane can be acquired (or timeout).
 * Used so APT popups retry after consent releases instead of skipping forever.
 */
import {
  acquireOverlay,
  subscribeOverlayClaims,
} from './overlayAdCoordinator';
import type { OverlayLane } from '../../shared/adsense/overlayPriority';

export function acquireOverlayWhenAvailable(
  lane: OverlayLane,
  id: string,
  options?: { timeoutMs?: number; signal?: { aborted: boolean } },
): Promise<boolean> {
  const timeoutMs = options?.timeoutMs ?? 45_000;

  return new Promise((resolve) => {
    let settled = false;
    let unsub: (() => void) | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const finish = (ok: boolean) => {
      if (settled) {
        return;
      }
      settled = true;
      if (unsub) {
        unsub();
      }
      if (timer) {
        clearTimeout(timer);
      }
      resolve(ok);
    };

    const tryAcquire = (): boolean => {
      if (options?.signal?.aborted) {
        finish(false);
        return true;
      }
      if (acquireOverlay(lane, id)) {
        finish(true);
        return true;
      }
      return false;
    };

    if (tryAcquire()) {
      return;
    }

    unsub = subscribeOverlayClaims(() => {
      tryAcquire();
    });

    timer = setTimeout(() => {
      if (options?.signal?.aborted) {
        finish(false);
        return;
      }
      finish(acquireOverlay(lane, id));
    }, timeoutMs);
  });
}
